import { createStore } from 'zustand';
import type { StoreApi } from 'zustand';
import type { HomeBase, StashCard, StashCategory, Stasher, Vendor } from '@stash/shared-types';
import { applyScope, applyHomeBase, haversineMeters } from './pipeline';
import type {
  DocumentChange,
  NearMeFilter,
  SortKey,
  StashCardPeopleScope,
  StashScopedActions,
  StashScopedState,
} from './types';

const DEFAULT_NEAR_ME_FILTER: NearMeFilter = {
  enabled: false,
  radiusMeters: 8047,
  userLocation: null,
};

function buildInitialState(
  id: string,
  currentUserStasherId: string,
  scope: StashCardPeopleScope,
  homeBase: HomeBase | null,
): StashScopedState {
  return {
    id,
    currentUserStasherId,
    scope,
    homeBase,
    sortKey: 'displayName',
    nearMeFilter: { ...DEFAULT_NEAR_ME_FILTER },
    // TODO(category-screen): the in-board "selectedCategoryId" drill-down was
    // replaced by the dedicated /app/categories/:id page. The field stays for
    // now because applyFilters/useFilteredStashCardViewModels/pipeline tests
    // still consume it; can be removed once those consumers are migrated.
    selectedCategoryId: null,
    allStashCards: [],
    scopedStashCards: [],
    stashers: [],
    vendors: [],
    categories: [],
    loading: false,
    isFirstLoadComplete: false,
  };
}

function scopeCards(
  cards: StashCard[],
  scope: StashCardPeopleScope,
  homeBase: HomeBase | null,
  currentUserStasherId: string,
): StashCard[] {
  let result = applyScope(cards, scope);
  if (homeBase) {
    result = applyHomeBase(result, homeBase, scope, currentUserStasherId);
  }
  return result;
}

export function createStashScopedStore(
  id: string,
  currentUserStasherId: string,
  scope?: StashCardPeopleScope,
  homeBase?: HomeBase | null,
): StoreApi<StashScopedState & StashScopedActions> {
  const initialScope: StashCardPeopleScope = scope ?? { kind: 'all' };
  const initialHomeBase: HomeBase | null = homeBase ?? null;

  // Closure refs — not reactive state
  let lastProcessedLocation: { lat: number; lng: number } | null = null;

  return createStore<StashScopedState & StashScopedActions>((set, get) => ({
    ...buildInitialState(id, currentUserStasherId, initialScope, initialHomeBase),

    initialize(
      cards: StashCard[],
      vendors: Vendor[],
      stashers: Stasher[],
      topCategories: StashCategory[],
    ): void {
      const { scope: currentScope, homeBase: currentHomeBase } = get();
      const scopedStashCards = scopeCards(cards, currentScope, currentHomeBase, currentUserStasherId);
      set({
        allStashCards: cards,
        scopedStashCards,
        vendors,
        stashers,
        categories: topCategories,
        loading: false,
        isFirstLoadComplete: true,
      });
    },

    applyChanges(changes: DocumentChange[]): void {
      const {
        allStashCards: currentCards,
        vendors: currentVendors,
        scope: currentScope,
        homeBase: currentHomeBase,
      } = get();
      const updatedCards = [...currentCards];
      let updatedVendors: Vendor[] = currentVendors;
      let vendorsChanged = false;

      const upsertVendor = (v: Vendor): void => {
        if (!vendorsChanged) {
          updatedVendors = [...currentVendors];
          vendorsChanged = true;
        }
        const idx = updatedVendors.findIndex(x => x.recordID === v.recordID);
        if (idx !== -1) updatedVendors[idx] = v;
        else updatedVendors.push(v);
      };
      const removeVendor = (id: string): void => {
        if (!vendorsChanged) {
          updatedVendors = [...currentVendors];
          vendorsChanged = true;
        }
        const idx = updatedVendors.findIndex(x => x.recordID === id);
        if (idx !== -1) updatedVendors.splice(idx, 1);
      };

      for (const change of changes) {
        if (change.type === 'deleted') {
          const idx = updatedCards.findIndex(c => c.recordID === change.cardId);
          if (idx !== -1) updatedCards.splice(idx, 1);
        } else if (change.type === 'added' || change.type === 'modified') {
          const card = change.card;
          if (card.recordStatus === 'deleted') {
            const idx = updatedCards.findIndex(c => c.recordID === card.recordID);
            if (idx !== -1) updatedCards.splice(idx, 1);
          } else {
            const idx = updatedCards.findIndex(c => c.recordID === card.recordID);
            if (idx !== -1) {
              updatedCards[idx] = card;
            } else {
              updatedCards.push(card);
            }
          }
        } else if (change.type === 'vendor-upserted') {
          upsertVendor(change.vendor);
        } else if (change.type === 'vendor-removed') {
          removeVendor(change.vendorId);
        }
      }

      const scopedStashCards = scopeCards(updatedCards, currentScope, currentHomeBase, currentUserStasherId);
      set({
        allStashCards: updatedCards,
        scopedStashCards,
        ...(vendorsChanged ? { vendors: updatedVendors } : {}),
      });
    },

    reset(): void {
      lastProcessedLocation = null;
      set(buildInitialState(id, currentUserStasherId, initialScope, initialHomeBase));
    },

    changeScope(newScope: StashCardPeopleScope): void {
      const { allStashCards, homeBase: currentHomeBase } = get();
      const scopedStashCards = scopeCards(allStashCards, newScope, currentHomeBase, currentUserStasherId);
      set({ scope: newScope, scopedStashCards });
    },

    setHomeBase(newHomeBase: HomeBase | null): void {
      const { allStashCards, scope: currentScope } = get();
      const scopedStashCards = scopeCards(allStashCards, currentScope, newHomeBase, currentUserStasherId);
      set({ homeBase: newHomeBase, scopedStashCards });
    },

    setNearMeFilter(filter: Partial<NearMeFilter>): void {
      const { nearMeFilter } = get();
      if (filter.userLocation !== undefined && filter.userLocation !== null) {
        if (
          lastProcessedLocation !== null &&
          haversineMeters(lastProcessedLocation, filter.userLocation) <= 10
        ) {
          return; // debounce — location within 10m, skip update
        }
        lastProcessedLocation = filter.userLocation;
      }
      set({ nearMeFilter: { ...nearMeFilter, ...filter } });
    },

    setSortKey(key: SortKey): void {
      set({ sortKey: key });
    },

    setSelectedCategory(id: string | null): void {
      set({ selectedCategoryId: id });
    },

    navigateCategoryBack(): void {
      const { selectedCategoryId, categories } = get();
      if (selectedCategoryId === null) return;
      const selected = categories.find(c => c.recordID === selectedCategoryId);
      set({ selectedCategoryId: selected?.parentCategoryID ?? null });
    },
  }));
}

